import { PROJECT_FILTER } from "types/project.types"

/**
 * Hierarchical react-query keys, one namespace per API domain.
 *
 * Invalidation cascades down the hierarchy: invalidating
 * `queryKeys.projects.root` invalidates every projects-related query
 * (lists, archived, folders, details, ...). Prefer invalidating the
 * narrowest key that covers the data a mutation changes.
 *
 * Extend with a new namespace per domain as migration proceeds
 * (users, documents, queries, reporting, ...).
 */
export const queryKeys = {
  projects: {
    root: ["projects"] as const,
    list: (filter: PROJECT_FILTER) => ["projects", "list", filter] as const,
    archived: ["projects", "archived"] as const,
    folders: ["projects", "folders"] as const,
    detail: (id: string) => ["projects", "detail", id] as const,
    members: (id: string) => ["projects", "members", id] as const,
    type: (id: string) => ["projects", "type", id] as const,
  },
  units: {
    root: ["units"] as const,
    users: ["units", "users"] as const,
  },
  documents: {
    root: ["documents"] as const,
    project: (projectId: string) => ["documents", projectId] as const,
    list: (projectId: string, filter: string, folder: string) =>
      ["documents", projectId, "list", filter, folder] as const,
  },
  automation: {
    root: ["automation"] as const,
    scripts: ["automation", "scripts"] as const,
    scriptMetadata: (name: string) =>
      ["automation", "scriptMetadata", name] as const,
    jobs: ["automation", "jobs"] as const,
    jobStatus: (jobId: string) => ["automation", "jobStatus", jobId] as const,
  },
  savedQueries: {
    root: ["savedQueries"] as const,
    project: (projectId: string, owner: string) =>
      ["savedQueries", projectId, owner] as const,
    list: (projectId: string, owner: string) =>
      ["savedQueries", projectId, owner, "list"] as const,
    detail: (projectId: string, owner: string, name: string) =>
      ["savedQueries", projectId, owner, "detail", name] as const,
  },
}
